// Pure helper functions for the ShipmentDashboard. Extracted from the main
// component so we can unit-test them in isolation and keep the dashboard
// file focused on data/UI wiring.

import { DEFAULT_STATUS_ROW_COLORS, MIN_COLUMN_WIDTHS, DARK_BACKGROUND_STATUSES } from './shipmentDashboardConstants';

// Normalize a status string for fuzzy matching against the colour map.
const norm = (s) => (s ? s.toLowerCase().replace(/\s+/g, ' ').trim() : '');

// Find a colour entry for `status` using exact match first, then a case- and
// whitespace-insensitive fallback so legacy statuses still render correctly.
const resolveStatusColour = (status, statusColors) => {
  let color = statusColors[status] || DEFAULT_STATUS_ROW_COLORS[status];
  if (!color && status) {
    const key = Object.keys(DEFAULT_STATUS_ROW_COLORS).find(
      (k) => norm(k) === norm(status),
    );
    if (key) color = statusColors[key] || DEFAULT_STATUS_ROW_COLORS[key];
  }
  return color;
};

// Row background colour for a given status. Dark mode tints with 40-alpha so
// the colour reads more as a hint than a flood-fill.
export const getRowBackgroundColor = (status, statusColors, isDark) => {
  const color = resolveStatusColour(status, statusColors);
  if (!color) return 'transparent';
  return isDark ? `${color}40` : color;
};

// Text colour for a row given its status + theme. Dark backgrounds in light
// mode flip to white text for legibility; dark mode inherits.
export const getTextColorForStatus = (status, statusColors, isDark) => {
  const bgColor = resolveStatusColour(status, statusColors);
  if (!bgColor) return 'inherit';
  const statusLower = (status || '').toLowerCase();
  if (DARK_BACKGROUND_STATUSES.some((s) => s.toLowerCase() === statusLower)) {
    return isDark ? 'inherit' : '#ffffff';
  }
  return isDark ? 'inherit' : '#1e293b';
};

// Default cell text colour when no per-cell custom colour is set.
export const getCellTextColor = (customColor, isDark) => {
  if (customColor) return customColor;
  return isDark ? '#94a3b8' : '#1e293b'; // slate-400 / slate-800
};

// Minimum column width with a sensible fallback for any new columns.
export const getMinColumnWidth = (columnId) => MIN_COLUMN_WIDTHS[columnId] || '60px';

// Flex column width — fixed on mobile, percentage-based on desktop when the
// table would overflow the viewport.
export const getFlexColumnWidth = (col, visibleColumns) => {
  if (typeof window === 'undefined') return col.width || 'auto';
  if (window.innerWidth < 768) return col.width || 'auto';

  const baseWidth = col.width || 100;
  const availableWidth = window.innerWidth - 200; // account for sidebar/padding
  const totalFixedWidth = visibleColumns.reduce((sum, c) => sum + (c.width || 100), 0);
  if (totalFixedWidth > availableWidth) {
    return `${(baseWidth / totalFixedWidth) * 100}%`;
  }
  return baseWidth;
};

// Calendar-day diff between LFD and today, timezone-safe. Accepts ISO strings,
// US m/d/y, or Date objects.
export const getDaysUntilLFD = (lfdDate) => {
  if (!lfdDate) return null;
  let lfd;
  if (typeof lfdDate === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(lfdDate)) {
      const year = parseInt(lfdDate.substring(0, 4), 10);
      const month = parseInt(lfdDate.substring(5, 7), 10);
      const day = parseInt(lfdDate.substring(8, 10), 10);
      lfd = new Date(year, month - 1, day);
    } else if (lfdDate.includes('/')) {
      const parts = lfdDate.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts.map(Number);
        lfd = new Date(year, month - 1, day);
      } else {
        lfd = new Date(lfdDate);
      }
    } else {
      lfd = new Date(lfdDate);
    }
  } else {
    lfd = new Date(lfdDate);
  }
  lfd.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((lfd - today) / (1000 * 60 * 60 * 24));
};

// LFD cell should blink amber/red when within 0-2 days (including today).
export const shouldLFDBlink = (lfdDate) => {
  const days = getDaysUntilLFD(lfdDate);
  return days !== null && days >= 0 && days <= 2;
};

// Format "HH:MM - HH:MM" or just "HH:MM" for time-window cells.
export const formatTimeWindow = (timeFrom, timeTo) => {
  if (!timeFrom) return '';
  return timeTo ? `${timeFrom} - ${timeTo}` : timeFrom;
};

// iter 264d.16 → 264d.17 — Merge a saved status_order with DEFAULT_STATUS_ORDER
// so any statuses added AFTER the user saved their preference still slot in
// stably instead of falling to the bottom via indexOf() === -1.
//
// Thin re-export around the generic `mergeWhitelistWithDefault` helper in
// utils/mergePrefsWithDefaults.js so every dashboard/preference type uses
// identical semantics.
export { mergeWhitelistWithDefault as mergeStatusOrderWithDefault } from '../../utils/mergePrefsWithDefaults';

// Sort comparator for `sortedShipments`. When `sortColumn` is set, sorts on
// that column; otherwise falls back to user-defined status order.
export const sortShipments = (shipments, sortColumn, sortDirection, statusOrder) => {
  const sorted = [...shipments];
  sorted.sort((a, b) => {
    if (sortColumn) {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Push empties to the end regardless of direction
      if (aVal === null || aVal === undefined || aVal === '') aVal = sortDirection === 'asc' ? 'zzzzz' : '';
      if (bVal === null || bVal === undefined || bVal === '') bVal = sortDirection === 'asc' ? 'zzzzz' : '';

      if (sortColumn.includes('date') || sortColumn === 'eta' || sortColumn === 'lfd' || sortColumn === 'appointment_date') {
        aVal = aVal ? new Date(aVal).getTime() : (sortDirection === 'asc' ? Infinity : 0);
        bVal = bVal ? new Date(bVal).getTime() : (sortDirection === 'asc' ? Infinity : 0);
      }

      if (typeof aVal === 'number' || sortColumn === 'weight') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }

    // Default: sort by status order (items not in order go to the end)
    const aIndex = statusOrder.indexOf(a.status);
    const bIndex = statusOrder.indexOf(b.status);
    const aOrder = aIndex === -1 ? 999 : aIndex;
    const bOrder = bIndex === -1 ? 999 : bIndex;
    return aOrder - bOrder;
  });
  return sorted;
};

// Look up the localized label for a column id; fall back to the supplied default.
export const buildColumnLabelResolver = (t) => (columnId, defaultLabel) => {
  const columnTranslations = {
    'actions': t('dashboard.columns.actions'),
    'container_number': t('dashboard.columns.containerNumber'),
    'container_size': 'Sz',
    'ssl': t('dashboard.columns.ssl'),
    'chassis': t('dashboard.columns.chassis'),
    'job_number': t('dashboard.columns.jobNumber'),
    'pp_ctf': t('dashboard.columns.ppCtf'),
    'status': t('dashboard.columns.status'),
    'eta': t('dashboard.columns.eta'),
    'appointment': t('dashboard.columns.appt'),
    'delivery_date': t('dashboard.columns.deliverDate'),
    'lfd': t('dashboard.columns.lfd'),
    'overweight': t('dashboard.columns.ow'),
    'hazmat': 'HZ',
    'mty_rdy': t('dashboard.columns.mtyRdy'),
    'mty_return': t('dashboard.columns.mtyReturn'),
    'return_location': 'RT',
    'yard': t('dashboard.columns.yard'),
    'customer': t('dashboard.columns.customer'),
    'deliver_to': t('dashboard.columns.deliverTo'),
    'driver': t('dashboard.columns.driver'),
    'terminal': t('dashboard.columns.terminal'),
    'mbl': t('dashboard.columns.mbl'),
    'pin': t('dashboard.columns.pin'),
    'customs_status': t('dashboard.columns.customs'),
    'pickup_date': t('dashboard.columns.pickup'),
    'docs': t('dashboard.columns.docs'),
    'remarks': t('dashboard.columns.remarks'),
  };
  return columnTranslations[columnId] || defaultLabel;
};
